import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { selectAllStocks, selectStockEntities } from '../slices/stocksSlice';
import { StockData } from '../../types';

const getStocksAdapterState = (state: any) => {
  // Defensive guard: some tests/partials can accidentally provide a shape
  // that doesn't include RTK entity adapter fields (`ids`/`entities`).
  if (!state || typeof state !== 'object') return undefined;
  if (!Array.isArray(state.ids)) return undefined;
  if (!state.entities || typeof state.entities !== 'object') return undefined;
  return state;
};

// Base stock selectors
export const selectStocks = (state: RootState) => {
  const slice = (state as any)?.stocks;
  const adapterState = getStocksAdapterState(slice);
  if (adapterState) return selectAllStocks(adapterState);

  // Fallback for tests/legacy state shapes where entity adapter fields are missing
  // but the slice still contains the plain `stocks` array.
  const legacyStocks = Array.isArray(slice?.stocks) ? slice.stocks : [];
  return legacyStocks;
};
export const selectStocksEntities = (state: RootState) => {
  const slice = (state as any)?.stocks;
  const adapterState = getStocksAdapterState(slice);
  if (adapterState) return selectStockEntities(adapterState);

  // Best-effort entity map for the subset of state shapes used in tests.
  const legacyStocks: StockData[] = Array.isArray(slice?.stocks) ? slice.stocks : [];
  return legacyStocks.reduce<Record<string, StockData>>((acc: Record<string, StockData>, stock: StockData) => {
    if (stock && typeof stock.ticker === 'string') acc[stock.ticker] = stock;
    return acc;
  }, {} as Record<string, StockData>);
};
export const selectStocksLoading = (state: RootState) => {
  const loading = (state as any)?.stocks?.loading;
  return typeof loading === 'boolean' ? loading : false;
};
export const selectSortConfig = (state: RootState) => {
  const sortConfig = (state as any)?.stocks?.sortConfig;
  if (!sortConfig || typeof sortConfig !== 'object') return { column: null, direction: 'asc' as const };
  return sortConfig;
};

// O(1) stock lookup by ticker
export const selectStockByTicker = createSelector(
  [selectStocksEntities, (state: RootState, ticker: string) => ticker],
  (stockEntities, ticker) => stockEntities[ticker]
);

// Optimized numeric columns set for sorting performance
const NUMERIC_COLUMNS = new Set([
  'current_price', 'day_change_percent', 'market_cap', 'ma_50', 'ma_100', 'ma_150', 'ma_200',
  'atr_percentage', 'target_percent', 'volume_surge',
  'trend_strength', 'composite_score', 'risk_reward_ratio', 'blended_rr',
  'confidence_level', 'target_price', 'score', 'entry_quality_score',
  // Darvas Box: flat 1-10 row alias of darvas_box_strength; sortable like score.
  // The generic sort path reads (stock as any)[sortColumn], so listing it here
  // gives it numeric comparison with missing rows pinned to the end.
  'darvas_score'
]);

// Convert any value to a number for sorting; treats null/undefined/NaN as
// "missing" by returning NaN, so callers can pin missing rows to the end of
// the sort regardless of direction.
const toSortNum = (v: unknown): number => {
  if (v == null) return NaN;
  if (typeof v === 'number') return v;
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? NaN : n;
};

// STRING_COLUMNS removed - was unused

// Create stable collator for string sorting
const createStableCollator = () => new Intl.Collator('en', {
  numeric: true,
  sensitivity: 'base',
  caseFirst: 'lower'
});

// Enhanced sorted stocks selector
export const selectSortedStocks = createSelector(
  [selectStocks, selectSortConfig],
  (stocks, sortConfig) => {
    if (stocks.length === 0) {
      return stocks;
    }

    // Default to score descending when no sort is specified but scores exist
    let column = sortConfig.column;
    let direction = sortConfig.direction;

    if (!column) {
      // Check if any stock has a score - if so, default to score descending
      const hasScore = stocks.some((stock: StockData) => stock.score !== undefined && stock.score !== null);
      if (hasScore) {
        column = 'score';
        direction = 'desc';
      } else {
        return stocks;
      }
    }
    const sortColumn: string = column;
    const isNumeric = NUMERIC_COLUMNS.has(sortColumn);
    const collator = isNumeric ? null : createStableCollator();
    const multiplier = direction === 'asc' ? 1 : -1;

    return [...stocks].sort((a, b) => {
      let aValue = (a as unknown as Record<string, unknown>)[sortColumn];
      let bValue = (b as unknown as Record<string, unknown>)[sortColumn];

      // Special handling for MA distance columns: sort by the backend-computed
      // ma_*_distance_pct (close-based, matches the displayed % badge) instead of
      // re-deriving from the now-LIVE current_price. Fall through to the numeric
      // path below so missing values sort last (not pinned at 0).
      if (sortColumn === 'ma_50') {
        aValue = a.ma_50_distance_pct;
        bValue = b.ma_50_distance_pct;
      } else if (sortColumn === 'ma_100') {
        aValue = a.ma_100_distance_pct;
        bValue = b.ma_100_distance_pct;
      } else if (sortColumn === 'ma_150') {
        aValue = a.ma_150_distance_pct;
        bValue = b.ma_150_distance_pct;
      } else if (sortColumn === 'ma_200') {
        aValue = a.ma_200_distance_pct;
        bValue = b.ma_200_distance_pct;
      }

      if (isNumeric) {
        // Numeric comparison; null/undefined/NaN always sort last regardless
        // of direction so missing rows don't interleave with real values.
        const aNum = toSortNum(aValue);
        const bNum = toSortNum(bValue);
        const aIsNull = Number.isNaN(aNum);
        const bIsNull = Number.isNaN(bNum);
        if (aIsNull && bIsNull) return 0;
        if (aIsNull) return 1;
        if (bIsNull) return -1;
        return (aNum - bNum) * multiplier;
      } else {
        // String comparison with collator, handle null/undefined by sorting them last
        const aStr = aValue != null ? String(aValue) : '';
        const bStr = bValue != null ? String(bValue) : '';

        // Sort empty values to the end
        if (!aStr && !bStr) return 0;
        if (!aStr) return 1 * multiplier;
        if (!bStr) return -1 * multiplier;

        return collator!.compare(aStr, bStr) * multiplier;
      }
    });
  }
);

// Enhanced filtered stocks with multiple search fields
export const selectFilteredStocks = createSelector(
  [selectStocks, (state: RootState, searchTerm?: string) => searchTerm],
  (stocks, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return stocks;

    const term = searchTerm.toLowerCase().trim();
    return stocks.filter((stock: StockData) =>
      stock.ticker.toLowerCase().includes(term) ||
      stock.company_name?.toLowerCase().includes(term) ||
      stock.sector?.toLowerCase().includes(term) ||
      stock.exchange?.toLowerCase().includes(term)
    );
  }
);

// Combined search and sort selector for optimal performance
export const selectFilteredAndSortedStocks = createSelector(
  [selectFilteredStocks, selectSortConfig],
  (filteredStocks, sortConfig) => {
    if (filteredStocks.length === 0) {
      return filteredStocks;
    }

    // Default to score descending when no sort is specified but scores exist
    let column = sortConfig.column;
    let direction = sortConfig.direction;

    if (!column) {
      // Check if any stock has a score - if so, default to score descending
      const hasScore = filteredStocks.some((stock: StockData) => stock.score !== undefined && stock.score !== null);
      if (hasScore) {
        column = 'score';
        direction = 'desc';
      } else {
        return filteredStocks;
      }
    }
    const sortColumn: string = column;
    const isNumeric = NUMERIC_COLUMNS.has(sortColumn);
    const collator = isNumeric ? null : createStableCollator();
    const multiplier = direction === 'asc' ? 1 : -1;

    return [...filteredStocks].sort((a, b) => {
      let aValue = (a as unknown as Record<string, unknown>)[sortColumn];
      let bValue = (b as unknown as Record<string, unknown>)[sortColumn];

      // Special handling for MA distance columns: sort by the backend-computed
      // ma_*_distance_pct (close-based, matches the displayed % badge) instead of
      // re-deriving from the now-LIVE current_price. Fall through to the numeric
      // path below so missing values sort last (not pinned at 0).
      if (sortColumn === 'ma_50') {
        aValue = a.ma_50_distance_pct;
        bValue = b.ma_50_distance_pct;
      } else if (sortColumn === 'ma_100') {
        aValue = a.ma_100_distance_pct;
        bValue = b.ma_100_distance_pct;
      } else if (sortColumn === 'ma_150') {
        aValue = a.ma_150_distance_pct;
        bValue = b.ma_150_distance_pct;
      } else if (sortColumn === 'ma_200') {
        aValue = a.ma_200_distance_pct;
        bValue = b.ma_200_distance_pct;
      }

      if (isNumeric) {
        // Numeric comparison; null/undefined/NaN always sort last regardless
        // of direction so missing rows don't interleave with real values.
        const aNum = toSortNum(aValue);
        const bNum = toSortNum(bValue);
        const aIsNull = Number.isNaN(aNum);
        const bIsNull = Number.isNaN(bNum);
        if (aIsNull && bIsNull) return 0;
        if (aIsNull) return 1;
        if (bIsNull) return -1;
        return (aNum - bNum) * multiplier;
      } else {
        // String comparison with collator, handle null/undefined by sorting them last
        const aStr = aValue != null ? String(aValue) : '';
        const bStr = bValue != null ? String(bValue) : '';

        // Sort empty values to the end
        if (!aStr && !bStr) return 0;
        if (!aStr) return 1 * multiplier;
        if (!bStr) return -1 * multiplier;

        return collator!.compare(aStr, bStr) * multiplier;
      }
    });
  }
);

// Stock statistics selector
export const selectStockStatistics = createSelector(
  [selectStocks],
  (stocks) => {
    if (stocks.length === 0) {
      return {
        totalCount: 0,
        passingCount: 0,
        passRate: 0,
        averageScore: 0,
        highQualityCount: 0,
        strongMomentumCount: 0,
        avgMarketCap: 0,
        avgPrice: 0
      };
    }

    const passingStocks = stocks.filter((stock: StockData) => stock.passing_filters);
    // Use new score field (1-10) for high quality: score >= 7 indicates top 10-20 stocks to buy
    const highQualityStocks = stocks.filter((stock: StockData) => (stock.score || 0) >= 7);
    const strongMomentumStocks = stocks.filter((stock: StockData) =>
      (stock.volume_surge || 0) > 1.5 &&
      stock.current_price > Math.max(stock.ma_50 || 0, stock.ma_150 || 0, stock.ma_200 || 0)
    );

    // Use new score field (1-10) for average score calculation
    const totalScore = stocks.reduce((sum: number, stock: StockData) => sum + (stock.score || 0), 0);
    const totalMarketCap = stocks.reduce((sum: number, stock: StockData) => sum + (stock.market_cap || 0), 0);
    const totalPrice = stocks.reduce((sum: number, stock: StockData) => sum + (stock.current_price || 0), 0);

    return {
      totalCount: stocks.length,
      passingCount: passingStocks.length,
      passRate: Math.round((passingStocks.length / stocks.length) * 100),
      averageScore: Math.round(totalScore / stocks.length),
      highQualityCount: highQualityStocks.length,
      strongMomentumCount: strongMomentumStocks.length,
      avgMarketCap: totalMarketCap / stocks.length,
      avgPrice: totalPrice / stocks.length
    };
  }
);

// Stocks grouped by pattern
export const selectStocksByPattern = createSelector(
  [selectStocks],
  (stocks) => {
    return stocks.reduce((acc: Record<string, StockData[]>, stock: StockData) => {
      const pattern = stock.pattern_match || 'unknown';
      if (!acc[pattern]) {
        acc[pattern] = [];
      }
      acc[pattern]?.push(stock);
      return acc;
    }, {} as Record<string, StockData[]>);
  }
);

// Performance metrics selector
export const selectPerformanceMetrics = createSelector(
  [selectStocks],
  (stocks) => {
    const validStocks = stocks.filter((stock: StockData) =>
      stock.current_price > 0 &&
      stock.target_percent != null &&
      !isNaN(stock.target_percent)
    );

    if (validStocks.length === 0) {
      return { avgTargetPercent: 0, medianTargetPercent: 0, topPerformers: [] };
    }

    const targetPercents = validStocks
      .map((stock: StockData) => stock.target_percent)
      .sort((a: number, b: number) => a - b);

    const avgTargetPercent = targetPercents.reduce((sum: number, val: number) => sum + val, 0) / targetPercents.length;
    const medianIndex = Math.floor(targetPercents.length / 2);
    const medianTargetPercent = targetPercents.length % 2 === 0
      ? ((targetPercents[medianIndex - 1] || 0) + (targetPercents[medianIndex] || 0)) / 2
      : targetPercents[medianIndex];

    const topPerformers = validStocks
      .filter((stock: StockData) => stock.target_percent > 10)
      .sort((a: StockData, b: StockData) => b.target_percent - a.target_percent)
      .slice(0, 5);

    return {
      avgTargetPercent: Math.round(avgTargetPercent * 100) / 100,
      medianTargetPercent: Math.round((medianTargetPercent || 0) * 100) / 100,
      topPerformers
    };
  }
);

// Stocks by sector selector
export const selectStocksBySector = createSelector(
  [selectStocks],
  (stocks) => {
    return stocks.reduce((acc: Record<string, StockData[]>, stock: StockData) => {
      const sector = stock.sector || 'Unknown';
      if (!acc[sector]) {
        acc[sector] = [];
      }
      acc[sector]?.push(stock);
      return acc;
    }, {} as Record<string, StockData[]>);
  }
);

// High momentum stocks selector
export const selectHighMomentumStocks = createSelector(
  [selectStocks],
  (stocks) => {
    return stocks.filter((stock: StockData) =>
      (stock.volume_surge || 0) > 1.5 &&
      stock.current_price > Math.max(stock.ma_50 || 0, stock.ma_150 || 0, stock.ma_200 || 0) &&
      (stock.trend_strength || 0) > 0.7
    ).sort((a: StockData, b: StockData) => (b.volume_surge || 0) - (a.volume_surge || 0));
  }
);

// Breakout candidates selector
export const selectBreakoutCandidates = createSelector(
  [selectStocks],
  (stocks) => {
    return stocks.filter((stock: StockData) => {
      const priceAboveMA = stock.current_price > Math.max(stock.ma_50 || 0, stock.ma_150 || 0, stock.ma_200 || 0);
      const highVolume = (stock.volume_surge || 0) > 1.3;
      const nearResistance = (stock.target_percent || 0) > 5 && (stock.target_percent || 0) < 15;

      return priceAboveMA && highVolume && nearResistance;
    }).sort((a: StockData, b: StockData) => (b.target_percent || 0) - (a.target_percent || 0));
  }
);

// Stocks with support lines selector
export const selectStocksWithSupportLines = createSelector(
  [selectStocks],
  (stocks) => {
    return stocks.filter((stock: StockData) =>
      stock.strong_support_result?.detected &&
      stock.strong_support_result?.support_line_coords
    );
  }
);